import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./cornhole.css";

const API = "/api/cornhole";

/* CSRF: EngPortal uses session auth, so every write needs the cookie token. */
function csrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? m[1] : "";
}

async function call(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = new Error((data && data.detail) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/* ---------- time helpers ---------- */
function clockFrom(startTime, offset) {
  if (offset == null || !startTime) return "—";
  const [h, m] = startTime.split(":").map(Number);
  const total = h * 60 + m + offset;
  const hh = Math.floor(total / 60) % 24;
  const mm = ((total % 60) + 60) % 60;
  const ap = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ap}`;
}

const amp = (label) => (label || "").replace(" / ", " & ");

/* ================= main ================= */
export default function CornholeTab() {
  const [view, setView] = useState("setup");
  const [tournaments, setTournaments] = useState([]);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [boardOpen, setBoardOpen] = useState(false);

  const flash = (kind, text) => setMsg({ kind, text });

  const loadAll = useCallback(async () => {
    try {
      const [list, hist] = await Promise.all([
        call("/tournaments/"),
        call("/history/"),
      ]);
      setTournaments(list.results || list);
      setHistory(hist.results || hist);
      const rows = list.results || list;
      if (rows.length && !current) {
        setCurrent(await call(`/tournaments/${rows[0].id}/`));
      }
    } catch (e) {
      flash("err", e.message);
    }
  }, [current]);

  useEffect(() => {
    loadAll();
  }, []);

  const refresh = async (id) => setCurrent(await call(`/tournaments/${id}/`));

  if (boardOpen && current) {
    return <ResultsBoard t={current} onClose={() => setBoardOpen(false)} />;
  }

  return (
    <div className="ch">
      <header className="ch-head">
        <p className="ch-eyebrow">AvCarb Material Solutions</p>
        <h1 className="ch-title">
          {current ? `${current.year} Cornhole Tournament` : "Cornhole Tournament"}
        </h1>
        <nav className="ch-nav" role="tablist">
          {[
            ["setup", "Setup"],
            ["run", "Run sheet"],
            ["bracket", "Bracket"],
            ["boards", "Boards"],
            ["history", "History"],
            ["results", "Results board"],
          ].map(([k, label]) => (
            <button
              key={k}
              role="tab"
              aria-selected={view === k}
              onClick={() => setView(k)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>

      {current && <MetaStrip t={current} />}
      {msg && (
        <div className={`ch-msg ch-${msg.kind}`} onClick={() => setMsg(null)}>
          {msg.text}
        </div>
      )}

      {view === "setup" && (
        <Setup
          tournaments={tournaments}
          current={current}
          setCurrent={setCurrent}
          busy={busy}
          setBusy={setBusy}
          flash={flash}
          onPick={async (id) => setCurrent(await call(`/tournaments/${id}/`))}
          onChanged={loadAll}
          refresh={refresh}
        />
      )}
      {view === "run" && current && (
        <RunSheet t={current} flash={flash} onScored={setCurrent} />
      )}
      {view === "bracket" && current && <Bracket t={current} />}
      {view === "boards" && current && <Boards t={current} />}
      {view === "history" && (
        <History history={history} current={current} flash={flash} reload={loadAll} />
      )}
      {view === "results" && current && (
        <ResultsPanel t={current} onOpen={() => setBoardOpen(true)} />
      )}
    </div>
  );
}

/* ================= meta ================= */
function MetaStrip({ t }) {
  const s = t.stats || {};
  const cells = [
    ["Players", s.players],
    ["Teams", s.teams],
    ["Bracket", s.bracket_size ? `${s.bracket_size}-slot` : "—"],
    ["Byes", s.byes],
    ["Games", s.games],
    ["Played", `${s.played} / ${s.games}`],
    ["Est. finish", clockFrom(t.start_time, s.finish_offset)],
    ["Draw seed", t.seed],
  ];
  return (
    <div className="ch-meta">
      {cells.map(([k, v]) => (
        <div key={k}>
          <span>{k}</span>
          <b>{v == null ? "—" : v}</b>
        </div>
      ))}
    </div>
  );
}

/* ================= setup ================= */
function Setup({ tournaments, current, setCurrent, busy, setBusy, flash, onPick, onChanged, refresh }) {
  const [form, setForm] = useState({
    year: new Date().getFullYear(),
    roster: "",
    boards: 4,
    game_minutes: 15,
    rest_minutes: 10,
    start_time: "13:00",
    odd_rule: "alt",
    third_place: "before",
    repeat_mode: "any",
    lookback_years: 1,
  });

  useEffect(() => {
    if (current) {
      setForm((f) => ({
        ...f,
        year: current.year,
        roster: (current.roster || []).join("\n"),
        boards: current.boards,
        game_minutes: current.game_minutes,
        rest_minutes: current.rest_minutes,
        start_time: (current.start_time || "13:00").slice(0, 5),
        odd_rule: current.odd_rule,
        third_place: current.third_place,
        repeat_mode: current.repeat_mode,
        lookback_years: current.lookback_years,
      }));
    }
  }, [current && current.id]);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "number" ? +e.target.value : e.target.value });

  const players = form.roster.split(/\r?\n/).map((s) => s.replace(/^\s*\d+[.)]\s*/, "").trim()).filter(Boolean);

  const payload = () => ({ ...form, roster: players });

  async function createOrUpdate() {
    setBusy(true);
    try {
      if (current && current.year === form.year) {
        await call(`/tournaments/${current.id}/`, { method: "PATCH", body: payload() });
        return current.id;
      }
      const made = await call("/tournaments/", { method: "POST", body: { ...payload(), seed: 0 } });
      return made.id;
    } finally {
      setBusy(false);
    }
  }

  async function draw(newSeed) {
    if (players.length < 4) {
      flash("err", "Add at least four players — that is the smallest field that makes a bracket.");
      return;
    }
    setBusy(true);
    try {
      const id = await createOrUpdate();
      try {
        await call(`/tournaments/${id}/draw/`, { method: "POST", body: { new_seed: newSeed } });
      } catch (e) {
        if (e.status === 409) {
          if (!window.confirm(`${e.message}\n\nDiscard the recorded scores and re-draw?`)) return;
          await call(`/tournaments/${id}/draw/`, {
            method: "POST",
            body: { new_seed: newSeed, confirm: true },
          });
        } else throw e;
      }
      await refresh(id);
      await onChanged();
      flash("ok", "Draw complete.");
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBusy(false);
    }
  }

  const dupes = [
    ...new Set(
      players.filter((n, i) => players.findIndex((x) => x.toLowerCase() === n.toLowerCase()) !== i)
    ),
  ];

  return (
    <>
      {tournaments.length > 0 && (
        <div className="ch-panel">
          <h2 className="ch-panel-hd">Tournaments</h2>
          <div className="ch-pillrow">
            {tournaments.map((t) => (
              <span key={t.id} className="ch-pillwrap" style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                <button
                  className={`ch-pillbtn${current && current.id === t.id ? " on" : ""}`}
                  onClick={() => onPick(t.id)}
                >
                  {t.year} · {t.status}
                </button>
                <button
                  className="ch-pill-del"
                  title={`Delete the ${t.year} tournament`}
                  aria-label={`Delete the ${t.year} tournament`}
                  disabled={busy}
                  onClick={async () => {
                    if (!window.confirm(`Delete the ${t.year} tournament? This removes its bracket and matches and cannot be undone. (Saved history is not affected.)`)) return;
                    setBusy(true);
                    try {
                      await call(`/tournaments/${t.id}/`, { method: "DELETE" });
                      flash("ok", `Deleted the ${t.year} tournament.`);
                      if (current && current.id === t.id) setCurrent(null);
                      await onChanged();
                    } catch (e) {
                      flash("err", e.message || "Delete failed.");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  style={{ background: "none", border: "none", color: "var(--ch-muted, #999)", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 4px" }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="ch-panel">
        <h2 className="ch-panel-hd">
          Set up the draw <small>one player per line</small>
        </h2>
        <div className="ch-setup">
          <div>
            <label htmlFor="ch-names">Player list</label>
            <textarea
              id="ch-names"
              spellCheck="false"
              value={form.roster}
              onChange={set("roster")}
              placeholder="Paste names here, one per line."
            />
            <p className="ch-note">{players.length} players</p>
            {dupes.length > 0 && (
              <div className="ch-warn">
                Repeated name{dupes.length > 1 ? "s" : ""}: <b>{dupes.join(", ")}</b>. Add an
                initial if these are different people, or delete the extra line.
              </div>
            )}
          </div>
          <div>
            <div className="ch-row2">
              <Field label="Year"><input type="number" value={form.year} onChange={set("year")} /></Field>
              <Field label="Boards"><input type="number" value={form.boards} onChange={set("boards")} /></Field>
            </div>
            <div className="ch-row2">
              <Field label="First throw"><input type="time" value={form.start_time} onChange={set("start_time")} /></Field>
              <Field label="Minutes / game"><input type="number" value={form.game_minutes} onChange={set("game_minutes")} /></Field>
            </div>
            <div className="ch-row2">
              <Field label="Rest between"><input type="number" value={form.rest_minutes} onChange={set("rest_minutes")} /></Field>
              <Field label="Odd headcount">
                <select value={form.odd_rule} onChange={set("odd_rule")}>
                  <option value="alt">Hold one as alternate</option>
                  <option value="trio">Make a team of three</option>
                </select>
              </Field>
            </div>
            <Field label="Third-place game">
              <select value={form.third_place} onChange={set("third_place")}>
                <option value="before">Play it right before the final</option>
                <option value="with">Run it alongside the final</option>
                <option value="none">Skip it</option>
              </select>
            </Field>
            <Field label="Repeat-pairing rule">
              <select value={form.repeat_mode} onChange={set("repeat_mode")}>
                <option value="any">No two past finalists on a team</option>
                <option value="exact">Only block the exact same team</option>
                <option value="off">No restriction</option>
              </select>
            </Field>
            <Field label="Look back how many years">
              <input type="number" value={form.lookback_years} onChange={set("lookback_years")} />
            </Field>
            <button className="ch-primary" disabled={busy} onClick={() => draw(true)}>
              {busy ? "Working…" : "Run the draw"}
            </button>
            {current && current.seed ? (
              <button className="ch-ghost" disabled={busy} onClick={() => draw(false)}>
                Rebuild from seed {current.seed}
              </button>
            ) : null}
            <p className="ch-note">
              The seed is the fingerprint of the draw. Rebuilding from a stored seed gives the
              same teams — that is how you settle any question about whether the draw was fair.
            </p>
          </div>
        </div>

        {current && current.blocked_pairs && current.blocked_pairs.length > 0 && (
          <div className="ch-ok">
            <b>
              {current.blocked_pairs.length} pairing
              {current.blocked_pairs.length > 1 ? "s" : ""} blocked
            </b>{" "}
            from past results:
            <div className="ch-pillrow">
              {current.blocked_pairs.map((p, i) => (
                <span className="ch-pill" key={i}>{p.join(" + ")}</span>
              ))}
            </div>
            {current.draw_clean ? (
              <div>Clean draw found on attempt {current.draw_attempts}.</div>
            ) : (
              <div className="ch-err">
                Could not honour the restriction with this field. Add players or relax the rule.
              </div>
            )}
          </div>
        )}
      </div>

      {current && current.teams && current.teams.length > 0 && (
        <div className="ch-panel">
          <h2 className="ch-panel-hd">
            Teams <small>{current.teams.length} teams from {current.stats.players} players</small>
          </h2>
          <div className="ch-teams">
            {current.teams.map((t) => (
              <div className={`ch-team${t.is_trio ? " trio" : ""}`} key={t.id}>
                <span className="num">{String(t.number).padStart(2, "0")}</span>
                <span className="players">
                  {t.label}
                  {current.stats.byes > 0 && t.number <= current.stats.byes && (
                    <span className="ch-byebadge">BYE R1</span>
                  )}
                  {t.is_trio && <i> · three-person team</i>}
                </span>
              </div>
            ))}
          </div>
          {current.alternate && (
            <div className="ch-warn">
              <b>{current.alternate}</b> drew the odd slot and is holding as alternate — first sub
              in if anyone drops.
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="ch-ctl">
      <label>{label}</label>
      {children}
    </div>
  );
}

/* ================= run sheet ================= */
function RunSheet({ t, flash, onScored }) {
  const [stamp, setStamp] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);

  const games = useMemo(
    () =>
      (t.matches || [])
        .filter((m) => m.is_game && m.start_offset != null)
        .sort((a, b) => a.start_offset - b.start_offset || a.board - b.board),
    [t]
  );
  const nextUp = games.find((m) => !m.winner && m.ready);

  async function record(m) {
    const d = draft[m.id] || {};
    const a = parseInt(d.a, 10);
    const b = parseInt(d.b, 10);
    if (!Number.isFinite(a) || !Number.isFinite(b)) {
      flash("err", `Enter both scores for ${m.code}.`);
      return;
    }
    setSaving(m.id);
    try {
      const updated = await call(`/matches/${m.id}/score/`, {
        method: "POST",
        body: { score_a: a, score_b: b, stamp_clock: stamp },
      });
      onScored(updated);
      setDraft({ ...draft, [m.id]: {} });
    } catch (e) {
      flash("err", e.message);
    } finally {
      setSaving(null);
    }
  }

  async function undo(m) {
    if (!window.confirm(`Clear ${m.code}? Anything decided after it is cleared too.`)) return;
    setSaving(m.id);
    try {
      onScored(await call(`/matches/${m.id}/score/`, { method: "DELETE" }));
    } catch (e) {
      flash("err", e.message);
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="ch-panel">
      <h2 className="ch-panel-hd">
        Run sheet <small>{t.stats.played} of {t.stats.games} games recorded</small>
      </h2>
      <label className="ch-check">
        <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} />
        Stamp the real finish time when I record a score
      </label>
      <table className="ch-table">
        <thead>
          <tr>
            <th>Time</th><th>Board</th><th>Match</th><th>Round</th>
            <th>Team A</th><th /><th>Team B</th><th /><th>Next</th><th />
          </tr>
        </thead>
        <tbody>
          {games.map((m) => {
            const done = !!m.winner;
            const wA = done && m.winner === m.team_a.id;
            const wB = done && m.winner === m.team_b.id;
            const d = draft[m.id] || {};
            return (
              <tr key={m.id} className={m === nextUp ? "live" : done ? "done" : ""}>
                <td className="mono">{clockFrom(t.start_time, m.start_offset)}</td>
                <td className="mono">B{m.board}</td>
                <td className="mono code">
                  {m.code}
                  {m.actual_end != null && (
                    <div className="sub">ended {clockFrom(t.start_time, m.actual_end)}</div>
                  )}
                </td>
                <td>{m.round_name}</td>
                <td className={wA ? "win" : ""}>{m.team_a.label}</td>
                <td className="sc">
                  {done ? <b>{m.score_a}</b> : m.ready ? (
                    <input type="number" inputMode="numeric" value={d.a || ""}
                      onChange={(e) => setDraft({ ...draft, [m.id]: { ...d, a: e.target.value } })} />
                  ) : <span className="box" />}
                </td>
                <td className={wB ? "win" : ""}>{m.team_b.label}</td>
                <td className="sc">
                  {done ? <b>{m.score_b}</b> : m.ready ? (
                    <input type="number" inputMode="numeric" value={d.b || ""}
                      onChange={(e) => setDraft({ ...draft, [m.id]: { ...d, b: e.target.value } })} />
                  ) : <span className="box" />}
                </td>
                <td className="mono">{m.next_code || (m.is_bronze ? "3rd" : "—")}</td>
                <td>
                  {done ? (
                    <button className="ch-ghost sm" disabled={saving === m.id} onClick={() => undo(m)}>Undo</button>
                  ) : m.ready ? (
                    <button className="ch-ghost sm" disabled={saving === m.id} onClick={() => record(m)}>
                      {saving === m.id ? "…" : "Record"}
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="ch-note">
        Higher score wins. Times ahead of the current game assume a full slot, so they re-flow as
        results come in.
      </p>
    </div>
  );
}

/* ================= bracket ================= */
function Bracket({ t }) {
  const rounds = useMemo(() => {
    const out = [];
    (t.matches || []).filter((m) => !m.is_bronze).forEach((m) => {
      (out[m.round_index] = out[m.round_index] || [])[m.index_in_round] = m;
    });
    return out;
  }, [t]);
  const bronze = (t.matches || []).find((m) => m.is_bronze);

  const MH = 90, GAP = 22, COLW = 220, GUT = 44, TOP = 34;
  const slot = MH + GAP;
  const y = [];
  y[0] = (rounds[0] || []).map((_, i) => i * slot);
  for (let r = 1; r < rounds.length; r++) {
    y[r] = rounds[r].map((_, i) => (y[r - 1][i * 2] + y[r - 1][i * 2 + 1]) / 2);
  }
  const height = (rounds[0] || []).length * slot + TOP + (bronze ? MH + 60 : 14);
  const width = rounds.length * (COLW + GUT) + COLW * 0.68;
  const podium = t.podium || {};

  return (
    <div className="ch-panel">
      <h2 className="ch-panel-hd">Bracket <small>single elimination · one game to 21</small></h2>
      <div className="ch-bracket-scroll">
        <div className="ch-bracket" style={{ width, height }}>
          {rounds.map((row, r) =>
            row.map((m, i) => {
              const x = r * (COLW + GUT);
              const top = y[r][i] + TOP;
              const cy = top + MH / 2;
              const parts = [
                <div className="ch-bmatch" key={m.code} style={{ left: x, top, width: COLW, height: MH }}>
                  <div className="bhd">
                    <span>{m.code}</span>
                    <span className="slot">
                      {m.is_game && m.board ? `B${m.board} · ${clockFrom(t.start_time, m.start_offset)}` : "bye"}
                    </span>
                  </div>
                  <BSlot m={m} side="a" />
                  <BSlot m={m} side="b" />
                </div>,
              ];
              if (r < rounds.length - 1) {
                const py = y[r + 1][Math.floor(i / 2)] + TOP + MH / 2;
                parts.push(<div className="ch-conn" key={m.code + "h"} style={{ left: x + COLW, top: cy, width: GUT / 2, height: 1 }} />);
                parts.push(<div className="ch-conn" key={m.code + "v"} style={{ left: x + COLW + GUT / 2, top: Math.min(cy, py), width: 1, height: Math.abs(py - cy) }} />);
                if (i % 2 === 0) parts.push(<div className="ch-conn" key={m.code + "j"} style={{ left: x + COLW + GUT / 2, top: py, width: GUT / 2, height: 1 }} />);
              }
              return parts;
            })
          )}
          <div
            className="ch-champ"
            style={{
              left: (rounds.length - 1) * (COLW + GUT) + COLW + GUT / 2,
              top: (y[rounds.length - 1] || [0])[0] + TOP - 2,
              width: COLW * 0.66,
              height: MH + 4,
            }}
          >
            <span className="lbl">CHAMPIONS</span>
            <div className="line">{podium.champion ? podium.champion.label : ""}</div>
          </div>
          {bronze && (
            <div
              className="ch-bmatch"
              style={{
                left: (rounds.length - 1) * (COLW + GUT),
                top: (y[rounds.length - 1] || [0])[0] + TOP + MH + 40,
                width: COLW,
                height: MH,
              }}
            >
              <div className="bhd">
                <span>THIRD PLACE</span>
                <span className="slot">
                  {bronze.board ? `B${bronze.board} · ${clockFrom(t.start_time, bronze.start_offset)}` : ""}
                </span>
              </div>
              <BSlot m={bronze} side="a" />
              <BSlot m={bronze} side="b" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BSlot({ m, side }) {
  const team = side === "a" ? m.team_a : m.team_b;
  const score = side === "a" ? m.score_a : m.score_b;
  const won = m.winner && team.id === m.winner;
  return (
    <div className={`ch-bslot${team.pending ? " pending" : ""}${won ? " won" : ""}`}>
      <span>{team.label}</span>
      <span className="sq">{score == null ? "" : score}</span>
    </div>
  );
}

/* ================= boards ================= */
function Boards({ t }) {
  const PPM = 6;
  const games = (t.matches || []).filter((m) => m.is_game && m.start_offset != null);
  const span = Math.max(60, ...games.map((m) => m.start_offset + t.game_minutes));
  const axis = [];
  for (let i = 0; i <= span; i += 15) axis.push(i);

  return (
    <div className="ch-panel">
      <h2 className="ch-panel-hd">
        Board loading{" "}
        <small>{t.boards} boards · {t.game_minutes} min per game · {games.length} games</small>
      </h2>
      <div className="ch-lanes-scroll">
        <div className="ch-lanes">
          {Array.from({ length: t.boards }, (_, i) => i + 1).map((b) => (
            <div className="ch-lane-row" key={b}>
              <div className="ch-lane-label">Board {b}</div>
              <div className="ch-lane-track" style={{ width: span * PPM + 40 }}>
                {games.filter((m) => m.board === b).map((m) => (
                  <div
                    key={m.code}
                    className="ch-blk"
                    data-state={m.winner ? "done" : m.round_index === 0 ? "open" : "later"}
                    style={{ left: m.start_offset * PPM, width: t.game_minutes * PPM - 3 }}
                  >
                    <div className="mid">{m.code}</div>
                    <div className="rd">{clockFrom(t.start_time, m.start_offset)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="ch-timeaxis">
          {axis.map((o) => <span key={o}>{clockFrom(t.start_time, o)}</span>)}
        </div>
      </div>
      <p className="ch-note">
        Grey blocks are finished. Later blocks assume a full slot and re-flow as scores come in.
      </p>
    </div>
  );
}

/* ================= history ================= */
function History({ history, current, flash, reload }) {
  async function saveYear() {
    if (!current) return;
    try {
      await call(`/tournaments/${current.id}/save-to-history/`, { method: "POST" });
      await reload();
      flash("ok", `${current.year} saved to history.`);
    } catch (e) {
      flash("err", e.message);
    }
  }

  async function setWinner(entry, index) {
    const champs = entry.finalists[index];
    const others = entry.finalists[1 - index];
    try {
      await call(`/history/${entry.id}/`, {
        method: "PATCH",
        body: { champions: champs, runners_up: others, note: "" },
      });
      await reload();
    } catch (e) {
      flash("err", e.message);
    }
  }

  return (
    <div className="ch-panel">
      <h2 className="ch-panel-hd">Past tournaments <small>drives the repeat-pairing rule</small></h2>
      {history.length === 0 && (
        <div className="ch-warn">
          No past tournaments on file. The repeat-pairing rule has nothing to work from.
        </div>
      )}
      {history.map((h) => (
        <div className="ch-hist" key={h.id}>
          <h4>{h.year}</h4>
          <Row k="Champions" v={h.champions && h.champions.join(" & ")} />
          <Row k="Runners-up" v={h.runners_up && h.runners_up.join(" & ")} />
          <Row k="Third" v={h.third_place && h.third_place.join(" & ")} />
          <Row k="Finalists" v={(h.finalists || []).map((f) => f.join(" & ")).join("   ·   ")} />
          {h.note && <p className="ch-note">{h.note}</p>}
          {!h.champions && (h.finalists || []).length === 2 && (
            <div className="ch-btnrow">
              <span className="ch-note">Who won?</span>
              {h.finalists.map((f, i) => (
                <button className="ch-ghost sm" key={i} onClick={() => setWinner(h, i)}>
                  {f.join(" & ")}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="ch-btnrow">
        <button className="ch-ghost" onClick={saveYear} disabled={!current}>
          Save this year's result
        </button>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="ch-hrow">
      <b>{k}</b>
      <span>{v || <i className="muted">not recorded</i>}</span>
    </div>
  );
}

/* ================= results ================= */
function ResultsPanel({ t, onOpen }) {
  const p = t.podium || {};
  const left = t.stats.games - t.stats.played;
  return (
    <div className="ch-panel">
      <h2 className="ch-panel-hd">Results board <small>for the internal video boards</small></h2>
      {p.champion ? (
        <div className="ch-ok">
          <b>{p.champion.label}</b> won the {t.year} tournament. The board is ready to display.
        </div>
      ) : (
        <div className="ch-warn">
          The board fills in as scores come in. {left} game{left === 1 ? "" : "s"} still to play.
        </div>
      )}
      <div className="ch-btnrow">
        <button className="ch-ghost" onClick={onOpen}>Open full-screen board</button>
      </div>
    </div>
  );
}

function ResultsBoard({ t, onClose }) {
  const p = t.podium || {};
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [onClose]);

  const rows = (t.matches || [])
    .filter((m) => m.winner)
    .sort((a, b) => b.round_index - a.round_index || a.order - b.order);

  return (
    <div className="ch-board">
      <button className="ch-board-exit" onClick={onClose}>Close</button>
      <div className="ch-board-top">
        <span>AvCarb Material Solutions</span>
        <span>{t.year} Tournament</span>
      </div>
      <p className="ch-board-congrats">Congratulations</p>
      <h2 className="ch-board-winners">{p.champion ? amp(p.champion.label) : "—"}</h2>
      <p className="ch-board-sub">
        {p.champion
          ? `Winners of the ${t.year} AvCarb Cornhole Tournament`
          : "Tournament in progress"}
      </p>
      <div className="ch-board-grid">
        <Card k="Champions" v={p.champion && amp(p.champion.label)} gold />
        <Card k="Runners-up" v={p.runner_up && amp(p.runner_up.label)} />
        <Card k="Third place" v={p.third && amp(p.third.label)} />
      </div>
      <table className="ch-board-res">
        <thead>
          <tr><th>Round</th><th>Winner</th><th>Score</th><th>Runner-up</th><th>Score</th></tr>
        </thead>
        <tbody>
          {rows.map((m) => {
            const aWon = m.winner === m.team_a.id;
            const w = aWon ? m.team_a : m.team_b;
            const l = aWon ? m.team_b : m.team_a;
            const ws = aWon ? m.score_a : m.score_b;
            const ls = aWon ? m.score_b : m.score_a;
            return (
              <tr key={m.code}>
                <td>{m.round_name}</td>
                <td className="w">{w.label}</td>
                <td className="s">{ws}</td>
                <td>{l.label}</td>
                <td className="s">{ls}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Card({ k, v, gold }) {
  return (
    <div className={`ch-board-card${gold ? " gold" : ""}`}>
      <div className="k">{k}</div>
      <div className="v">{v || "—"}</div>
    </div>
  );
}
